import type { DataGridLeafColumn, DataGridSortingState } from '@ibsys/kit';
import { useMemo, useState } from 'react';
import type { CustomChartTableColumn, CustomChartTableRow } from '@/entities/dashboards';
import { fmt } from '../../utils';
import { chartDatePrecision, compareChartValues, formatChartDate, isChartDateType } from '../chartValues';

/** Ширина колонки таблицы: числовая уже текстовой — в ней значение, а не название. */
const TEXT_COLUMN_SIZE = 220;
const NUMBER_COLUMN_SIZE = 140;

/** Ширина колонки с датой: запись `дд.мм.гггг чч:мм:сс` шире числа, но уже названия. */
const DATE_COLUMN_SIZE = 180;

/** Строка таблицы вместе с её порядковым ключом: значения строк могут совпадать целиком. */
export type IndexedTableRow = { key: string; values: CustomChartTableRow };

/** Ширина колонки по типу её значений. */
const columnSize = (type: CustomChartTableColumn['type']): number => {
  if (type === 'number') return NUMBER_COLUMN_SIZE;
  return isChartDateType(type) ? DATE_COLUMN_SIZE : TEXT_COLUMN_SIZE;
};

/**
 * Строит определения колонок DataGrid по описанию колонок ответа.
 *
 * Значение колонки-даты показывается в виде, принятом для пользователя, а упорядочивается по
 * машинной записи: она и остаётся значением ячейки, а вид задаётся только показом. Ряд, по
 * которому берётся точность времени, — сама колонка: значения одной колонки написаны одинаково,
 * а соседняя колонка дат может быть написана иначе.
 */
export const buildTableColumns = (
  columns: CustomChartTableColumn[],
  rows: CustomChartTableRow[],
): DataGridLeafColumn<IndexedTableRow>[] =>
  columns.map((column) => {
    const precision = chartDatePrecision(
      rows.map((row) => row[column.code]),
      column.type,
    );

    return {
      id: column.code,
      header: column.title,
      accessor: (row) => row.values[column.code] ?? null,
      size: columnSize(column.type),
      align: column.type === 'number' ? 'center' : undefined,
      enableSorting: true,
      cell: ({ value }) => {
        if (column.type === 'number') return fmt(value as number | null);
        if (value === null || value === undefined) return '';
        return isChartDateType(column.type) ? formatChartDate(String(value), column.type, precision) : String(value);
      },
    };
  });

/** Приписывает строкам ключ по их позиции — одинаковые по значениям строки остаются различимы. */
export const toIndexedRows = (rows: CustomChartTableRow[], prefix = ''): IndexedTableRow[] =>
  rows.map((values, index) => ({ key: `${prefix}${index}`, values }));

/**
 * Упорядочение строк таблицы диаграммы: колонку выбирает пользователь, порядок держится за
 * значениями её типа — датами, числами или текстом.
 *
 * Данные диаграммы целиком у клиента, поэтому упорядочение идёт здесь же, без обращения к
 * бэкенду: заново наполнять диаграмму, чтобы переставить те же строки, незачем.
 */
export const useSortedTableRows = (rows: IndexedTableRow[], columns: CustomChartTableColumn[]) => {
  const [sorting, setSorting] = useState<DataGridSortingState>([]);

  const sortedRows = useMemo(() => {
    const [order] = sorting;
    const column = order && columns.find((item) => item.code === order.columnId);
    if (!order || !column) return rows;
    const direction = order.direction === 'desc' ? -1 : 1;
    return [...rows].sort(
      (first, second) =>
        direction *
        compareChartValues(first.values[column.code] ?? null, second.values[column.code] ?? null, column.type),
    );
  }, [rows, columns, sorting]);

  return { rows: sortedRows, sorting, onSortingChange: setSorting };
};
